import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Text } from '~/eds/src/components/Text/Text';

import { getNodeIconName } from '../helpers/getResourceIcon';
import { EnrichedGroupNode } from '../helpers/NodeEnricher/NodeEnricher';
import { TreeBrowserNode } from '../helpers/TreeBrowser/types';

const messages = defineMessages({
  selectedItemsCount: {
    id: 'AddItemsPanel.selectedItemsCount',
    defaultMessage: '{count} items selected',
    description: 'Count of selected items',
  },
  parentNodeLabelOptional: {
    id: 'AddItemsPanel.parentNodeLabel',
    defaultMessage: 'Parent Node (Optional)',
    description: 'Label for parent node dropdown',
  },
  parentNodeLabelRequired: {
    id: 'AddItemsPanel.parentNodeLabelRequired',
    defaultMessage: 'Parent Node',
    description: 'Label for parent node dropdown',
  },
  parentNodePlaceholder: {
    id: 'AddItemsPanel.parentNodePlaceholder',
    defaultMessage: 'Select a parent node...',
    description: 'Placeholder for parent node dropdown',
  },
  untitledItem: {
    id: 'AddItemsPanel.untitledItem',
    defaultMessage: 'Untitled',
    description: 'Label for untitled item',
  },
  disabledNodesInfo: {
    id: 'AddItemsPanel.disabledNodesInfo',
    defaultMessage:
      'Items cannot be moved to children of selected items and will be disabled.',
    description: 'Info message explaining why some nodes are disabled',
  },
});

export interface SelectParentStepProps {
  isVisible: boolean;
  selectedParentNode: string | null;
  selectedIds: string[];
  selectionRequired?: boolean;
  disableNodeIds?: string[];
  showDisabledNodesInfo?: boolean;
  onSelectionChange: (selectedParentNode: string | null) => void;
  onLoadAllNodes?: () => Promise<TreeBrowserNode<EnrichedGroupNode>[]>;
}

export const SelectParentStep = ({
  isVisible,
  disableNodeIds,
  selectedIds,
  selectedParentNode,
  selectionRequired = true,
  showDisabledNodesInfo = false,
  onSelectionChange,
  onLoadAllNodes,
}: SelectParentStepProps) => {
  const { formatMessage } = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [nodes, setNodes] = useState<TreeBrowserNode<EnrichedGroupNode>[]>([]);
  const disabledNodeIdsMap = useMemo(
    () =>
      disableNodeIds?.reduce(
        (acc, id) => {
          acc[id] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      ),
    [disableNodeIds],
  );

  const loadParentOptions = useCallback(() => {
    setIsLoading(true);
    onLoadAllNodes?.().then((nodes) => {
      setNodes(nodes);
      setIsLoading(false);
    });
  }, [onLoadAllNodes]);

  const options = useMemo(() => {
    return nodes?.map((node) => ({
      label: node.enrichedFields?.name ?? formatMessage(messages.untitledItem),
      value: node.id,
      leftIcon: getNodeIconName(node),
      disabled: disabledNodeIdsMap?.[node.id],
    }));
  }, [nodes, disabledNodeIdsMap]);

  return (
    <Layout
      direction="column"
      spacing={4}
      display={isVisible ? 'flex' : 'none'}
    >
      <Text color="text.secondary">
        {formatMessage(messages.selectedItemsCount, {
          count: selectedIds.length,
        })}
      </Text>
      {showDisabledNodesInfo && (
        <StatusMessage
          status="info"
          message={formatMessage(messages.disabledNodesInfo)}
        />
      )}
      <FormField<string, false>
        input={Select}
        inputProps={{
          isLoading,
          isSearchable: true,
          enableSearchIcon: true,
          onFocus: loadParentOptions,
          options,
          placeholder: formatMessage(messages.parentNodePlaceholder),
          isClearable: true,
        }}
        label={formatMessage(
          selectionRequired
            ? messages.parentNodeLabelRequired
            : messages.parentNodeLabelOptional,
        )}
        name="parent-node"
        value={selectedParentNode}
        onChange={onSelectionChange}
      />
    </Layout>
  );
};
